import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import NewsCard from '@/components/feature/NewsCard';
import HotRank from '@/pages/home/components/HotRank';
import { channels, gossipItems, type ChannelKey } from '@/mocks/gossip';
import { useSeo } from '@/hooks/useSeo';

export default function Channel() {
  const { slug } = useParams<{ slug: string }>();
  const channel = channels.find((c) => c.key === slug);

  useSeo({
    title: channel ? `${channel.label}频道 | 吃瓜日报今日更新` : '频道不存在 | 吃瓜日报',
    description: channel
      ? `${channel.label}频道内容合集：${channel.desc}。吃瓜日报每日更新，速览该领域的瓜料与热门事件。`
      : '你访问的频道不存在，返回首页继续吃瓜。',
    keywords: channel ? `${channel.label},吃瓜日报,热门事件` : '吃瓜日报',
    type: 'website',
    jsonLd: channel
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${channel.label}频道`,
            description: channel.desc,
          },
        ]
      : [],
  });

  if (!channel) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          <h1 className="font-heading text-3xl font-black text-foreground-900">频道不存在</h1>
          <p className="mt-3 text-sm text-foreground-500">这个频道还没开通，先回首页看看别的瓜吧。</p>
          <Link to="/" className="mt-6 inline-flex h-11 items-center rounded-full bg-primary-500 px-6 text-sm font-bold text-background-50 hover:bg-primary-600">
            回到首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const items = gossipItems
    .filter((i) => i.channel === (slug as ChannelKey))
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <section className="w-full bg-primary-600 text-background-50">
          <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
            <nav className="text-xs text-primary-100" aria-label="面包屑">
              <Link to="/" className="hover:text-background-50">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-background-50">{channel.label}</span>
            </nav>
            <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-center">
              <span className="grid h-16 w-16 items-center justify-center rounded-2xl bg-background-50 text-3xl text-primary-600">
                <i className={channel.icon}></i>
              </span>
              <div>
                <h1 className="font-heading text-3xl font-black text-background-50 md:text-4xl">
                  {channel.label}频道
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-primary-100">{channel.desc}</p>
              </div>
            </div>
            <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-bold text-background-50">
              <i className="ri-refresh-line"></i> 已更新 {items.length} 条内容
            </p>
          </div>
        </section>

        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {items.map((it) => (
                <NewsCard key={it.slug} item={it} />
              ))}
            </div>
            {items.length === 0 && (
              <div className="rounded-xl border border-dashed border-background-300 bg-background-50 p-10 text-center">
                <p className="text-sm text-foreground-500">这个频道的内容还在路上，稍后再来看看吧。</p>
              </div>
            )}
          </div>

          <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">
            <HotRank />
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-black text-foreground-900">其他频道</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {channels.filter((c) => c.key !== channel.key).map((c) => (
                  <Link key={c.key} to={`/channel/${c.key}`} className="rounded-full border border-background-200 bg-background-100 px-3 py-1.5 text-xs font-bold text-foreground-600 hover:border-primary-300 hover:text-primary-700">
                    {c.label}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}